import React, { useState } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select';
import PortalTooltip from './PortalTooltip';

const parseNum = (str) => {
  if (!str) return 0;
  if (typeof str === 'number') return str;
  const num = parseFloat(String(str).replace(/,/g, '').replace(/\$|B|M|K|%/g, (match) => {
    switch (match) {
      case 'B': return 'e9';
      case 'M': return 'e6';
      case 'K': return 'e3';
      case '%': return '';
      default: return '';
    }
  }));
  return isNaN(num) ? 0 : num;
};

const PeerRankingStrip = ({ entities, analysisRecords = [], isPeerMode }) => {
  const [hoveredEntityName, setHoveredEntityName] = useState(null);
  const [metric, setMetric] = useState('total_pubs');

  // Sort data by metric to make it a ranking
  const sortedData = [...entities].map(e => {
    const record = analysisRecords.find(r => r.id === e.id);
    return {
      ...e,
      [metric]: parseNum(record?.[metric])
    };
  }).sort((a, b) => b[metric] - a[metric]);

  const METRIC_LABELS = {
    total_pubs: "Publication Count",
    total_citations: "Citation Count",
    total_grant: "Grants",
    total_grant_amount: "Amount Awarded",
    faculty_count: "Faculty Count",
    active_researchers: "Active Researchers",
    h_index: "H-Index",
    h5_index: "H5-Index",
    h10_index: "H10-Index",
    i10_index: "i10-Index",
    g_index: "G-Index",
    median_citations_per_paper: "Median Citations / Paper",
    pct_cited_papers: "% Cited Papers",
    pct_top10_cited: "% in Top 10% Cited",
    pct_top1_cited: "% in Top 1% Cited",
    pubs_per_faculty: "Publications / Faculty",
    pubs_per_active_researcher: "Publications / Active Researcher",
    median_pubs_per_researcher: "Median Publications / Researcher",
    avg_pubs_per_year: "Average Publications / Year",
    recent_5yr_share: "Recent 5-yr Publication Share",
    pub_cagr: "Publication CAGR",
    int_collab_countries: "Collaborating Countries"
  };

  const CustomTooltip = ({ active, payload, coordinate }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      const val = payload[0].value;
      const isBase = data.isBaseEntity;
      const isHovered = hoveredEntityName === data.name;
      const shouldHighlight = isBase || isHovered;

      return (
        <PortalTooltip active={active} payload={payload} coordinate={coordinate}>
          <p className="mb-1" style={{ color: data.color, fontWeight: shouldHighlight ? 'bold' : 'normal' }}>
            {data.name}
          </p>
          <p className="text-slate-600">
            {METRIC_LABELS[metric]}: <span className="font-medium text-slate-800">{val?.toLocaleString()}</span>
          </p>
        </PortalTooltip>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-full flex flex-col">
      <div className="flex flex-col xl:flex-row items-center justify-between gap-3 mb-4">
        <div className="text-center xl:text-left">
          <h3 className="text-lg font-semibold text-gray-800">{METRIC_LABELS[metric]} Comparison</h3>
          <p className="text-xs text-slate-500">Ranking selected entities</p>
        </div>
        <div className="flex items-center shrink-0">
          <Select value={metric} onValueChange={setMetric}>
          <SelectTrigger className="w-[180px] h-8 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="total_pubs">Publication Count</SelectItem>
            <SelectItem value="total_citations">Citation Count</SelectItem>
            <SelectItem value="total_grant">Grants</SelectItem>
            <SelectItem value="total_grant_amount">Amount Awarded</SelectItem>
            <SelectItem value="faculty_count">Faculty Count</SelectItem>
            <SelectItem value="active_researchers">Active Researchers</SelectItem>
            <SelectItem value="h_index">H-Index</SelectItem>
            <SelectItem value="h5_index">H5-Index</SelectItem>
            <SelectItem value="h10_index">H10-Index</SelectItem>
            <SelectItem value="i10_index">i10-Index</SelectItem>
            <SelectItem value="g_index">G-Index</SelectItem>
            <SelectItem value="median_citations_per_paper">Median Citations / Paper</SelectItem>
            <SelectItem value="pct_cited_papers">% Cited Papers</SelectItem>
            <SelectItem value="pct_top10_cited">% in Top 10% Cited</SelectItem>
            <SelectItem value="pct_top1_cited">% in Top 1% Cited</SelectItem>
            <SelectItem value="pubs_per_faculty">Publications / Faculty</SelectItem>
            <SelectItem value="pubs_per_active_researcher">Publications / Active Researcher</SelectItem>
            <SelectItem value="median_pubs_per_researcher">Median Publications / Researcher</SelectItem>
            <SelectItem value="avg_pubs_per_year">Average Publications / Year</SelectItem>
            <SelectItem value="recent_5yr_share">Recent 5-yr Publication Share</SelectItem>
            <SelectItem value="pub_cagr">Publication CAGR</SelectItem>
            <SelectItem value="int_collab_countries">Collaborating Countries</SelectItem>
          </SelectContent>
        </Select>
        </div>
      </div>
      
      <div className="flex-1 w-full min-h-[300px]">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={sortedData} layout="vertical" margin={{ top: 0, right: 20, left: 20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f1f5f9" />
            <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} />
            <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#64748b" }} width={80} />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f8fafc' }} />
            <Bar dataKey={metric} radius={[0, 4, 4, 0]} maxBarSize={32}>
              {sortedData.map((entry, index) => {
                const isHovered = hoveredEntityName === entry.name;
                const isAnyHovered = hoveredEntityName !== null;
                
                let opacity = 1;
                let strokeW = 0;
                let strokeColor = "none";
                
                if (isPeerMode) {
                  if (entry.isBaseEntity) {
                    opacity = 1;
                    strokeW = 1;
                    strokeColor = "#000";
                  } else {
                    opacity = isHovered ? 1 : 0.25;
                    strokeW = isHovered ? 1 : 0;
                    strokeColor = isHovered ? "#000" : "none";
                  }
                } else {
                  opacity = 1;
                  strokeW = 0;
                  strokeColor = "none";
                }
                
                return (
                <Cell 
                  key={`cell-${index}`} 
                  fill={entry.color} 
                  fillOpacity={opacity}
                  stroke={strokeColor}
                  strokeWidth={strokeW}
                  onMouseEnter={() => setHoveredEntityName(entry.name)}
                  onMouseLeave={() => setHoveredEntityName(null)}
                />
              )})}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default PeerRankingStrip;
